// Content for "The Name" (etymology) and "Distribution" sections, in all 6 languages.
// Term words (Clann, Mac, Dòmhnall) stay Gaelic; glosses/bodies are translated.
export const heritage = {
  en: {
    name: {
      overline: "The Name",
      title: "Clann · Mhic · Dòmhnaill",
      intro: "The clan's Gaelic name is built from three words. Together they say exactly who the McDonalds are — the children of the son of Donald.",
      terms: [
        { term: "Clann", gloss: "children · kindred", body: "An old Gaelic word for children and offspring — and so a clan: a family descended from one ancestor." },
        { term: "Mc / Mac / Mhic", gloss: "son of", body: "“Son.” In the possessive it softens to Mhic (“of the son of”); the feminine form is Nic (“daughter of”)." },
        { term: "Dòmhnall", gloss: "Donald · “world-ruler”", body: "The name Donald, from Old Gaelic Domnall — domno “world” + val “rule, might.” Its possessive form is Dòmhnaill." },
      ],
      compareA: "Clann Dòmhnaill",
      compareAMeaning: "“the children of Donald”",
      compareB: "Clann Mhic Dhòmhnaill",
      compareBMeaning: "“the children of the son of Donald” — the descendants of MacDonald",
      note: "From Mac Dhòmhnaill came the McDonalds and MacDonalds surnames; the Irish branch became Mac Domhnaill → MacDonnell.",
    },
    distribution: {
      overline: "Distribution",
      title: "A clan spread across the world.",
      intro: "Counted together, the McDonalds are among the most numerous names of Scottish and Irish origin on earth.",
      worldwideLabel: "McDonalds worldwide",
      worldwideNote: "McDonald and MacDonald combined — over 600,000 counting every spelling.",
      statScotland: "Scotland's 2nd most common surname (MacDonald + McDonald)",
      statUS: "in the United States — the largest single population",
      statIreland: "across Ireland, often carried as MacDonnell",
      countriesTitle: "Where the McDonalds live",
      spellingTitle: "By spelling",
      source: "Estimates: Forebears (McDonald & MacDonald) and National Records of Scotland.",
    },
  },

  fr: {
    name: {
      overline: "Le Nom",
      title: "Clann · Mhic · Dòmhnaill",
      intro: "Le nom gaélique du clan se compose de trois mots. Ensemble, ils disent exactement qui sont les McDonald — les enfants du fils de Donald.",
      terms: [
        { term: "Clann", gloss: "enfants · parenté", body: "Un ancien mot gaélique pour enfants et descendance — et donc un clan : une famille issue d'un même ancêtre." },
        { term: "Mc / Mac / Mhic", gloss: "fils de", body: "« Fils ». Au possessif, il s'adoucit en Mhic (« du fils de ») ; la forme féminine est Nic (« fille de »)." },
        { term: "Dòmhnall", gloss: "Donald · « maître du monde »", body: "Le nom Donald, du vieux gaélique Domnall — domno « monde » + val « pouvoir ». Sa forme possessive est Dòmhnaill." },
      ],
      compareA: "Clann Dòmhnaill",
      compareAMeaning: "« les enfants de Donald »",
      compareB: "Clann Mhic Dhòmhnaill",
      compareBMeaning: "« les enfants du fils de Donald » — les descendants de MacDonald",
      note: "De Mac Dhòmhnaill sont venus les noms Macdonald et McDonald ; la branche irlandaise est devenue Mac Domhnaill → MacDonnell.",
    },
    distribution: {
      overline: "Répartition",
      title: "Un clan répandu dans le monde.",
      intro: "Comptés ensemble, les McDonald figurent parmi les noms d'origine écossaise et irlandaise les plus nombreux au monde.",
      worldwideLabel: "McDonald dans le monde",
      worldwideNote: "McDonald et MacDonald réunis — plus de 600 000 toutes graphies confondues.",
      statScotland: "2ᵉ nom le plus courant d'Écosse (MacDonald + McDonald)",
      statUS: "aux États-Unis — la plus grande population",
      statIreland: "à travers l'Irlande, souvent porté sous la forme MacDonnell",
      countriesTitle: "Où vivent les McDonald",
      spellingTitle: "Par graphie",
      source: "Estimations : Forebears (McDonald & MacDonald) et National Records of Scotland.",
    },
  },

  es: {
    name: {
      overline: "El Nombre",
      title: "Clann · Mhic · Dòmhnaill",
      intro: "El nombre gaélico del clan se compone de tres palabras. Juntas dicen exactamente quiénes son los McDonald: los hijos del hijo de Donald.",
      terms: [
        { term: "Clann", gloss: "hijos · parentela", body: "Una antigua palabra gaélica para hijos y descendencia — y por tanto un clan: una familia descendiente de un solo antepasado." },
        { term: "Mc / Mac / Mhic", gloss: "hijo de", body: "«Hijo». En el posesivo se suaviza a Mhic («del hijo de»); la forma femenina es Nic («hija de»)." },
        { term: "Dòmhnall", gloss: "Donald · «señor del mundo»", body: "El nombre Donald, del gaélico antiguo Domnall — domno «mundo» + val «poder». Su forma posesiva es Dòmhnaill." },
      ],
      compareA: "Clann Dòmhnaill",
      compareAMeaning: "«los hijos de Donald»",
      compareB: "Clann Mhic Dhòmhnaill",
      compareBMeaning: "«los hijos del hijo de Donald» — los descendientes de MacDonald",
      note: "De Mac Dhòmhnaill surgieron los apellidos Macdonald y McDonald; la rama irlandesa se volvió Mac Domhnaill → MacDonnell.",
    },
    distribution: {
      overline: "Distribución",
      title: "Un clan extendido por el mundo.",
      intro: "Contados juntos, los McDonald están entre los nombres de origen escocés e irlandés más numerosos del mundo.",
      worldwideLabel: "McDonald en el mundo",
      worldwideNote: "McDonald y MacDonald juntos — más de 600.000 contando cada grafía.",
      statScotland: "2.º apellido más común de Escocia (MacDonald + McDonald)",
      statUS: "en los Estados Unidos — la mayor población",
      statIreland: "por toda Irlanda, a menudo como MacDonnell",
      countriesTitle: "Dónde viven los McDonald",
      spellingTitle: "Por grafía",
      source: "Estimaciones: Forebears (McDonald y MacDonald) y National Records of Scotland.",
    },
  },

  de: {
    name: {
      overline: "Der Name",
      title: "Clann · Mhic · Dòmhnaill",
      intro: "Der gälische Clanname besteht aus drei Wörtern. Zusammen sagen sie genau, wer die McDonalds sind — die Kinder des Sohnes von Donald.",
      terms: [
        { term: "Clann", gloss: "Kinder · Sippe", body: "Ein altes gälisches Wort für Kinder und Nachkommen — und damit ein Clan: eine Familie, die von einem Ahnen abstammt." },
        { term: "Mc / Mac / Mhic", gloss: "Sohn von", body: "„Sohn.“ Im Genitiv wird es zu Mhic („des Sohnes von“) erweicht; die weibliche Form ist Nic („Tochter von“)." },
        { term: "Dòmhnall", gloss: "Donald · „Weltenherrscher“", body: "Der Name Donald, vom altgälischen Domnall — domno „Welt“ + val „Herrschaft“. Seine Genitivform ist Dòmhnaill." },
      ],
      compareA: "Clann Dòmhnaill",
      compareAMeaning: "„die Kinder Donalds“",
      compareB: "Clann Mhic Dhòmhnaill",
      compareBMeaning: "„die Kinder des Sohnes von Donald“ — die Nachkommen von MacDonald",
      note: "Aus Mac Dhòmhnaill entstanden die Namen Macdonald und McDonald; der irische Zweig wurde Mac Domhnaill → MacDonnell.",
    },
    distribution: {
      overline: "Verbreitung",
      title: "Ein Clan, über die Welt verstreut.",
      intro: "Zusammengezählt gehören die McDonalds zu den zahlreichsten Namen schottischen und irischen Ursprungs auf der Erde.",
      worldwideLabel: "McDonalds weltweit",
      worldwideNote: "McDonald und MacDonald zusammen — über 600.000 mit allen Schreibweisen.",
      statScotland: "zweithäufigster Nachname Schottlands (MacDonald + McDonald)",
      statUS: "in den Vereinigten Staaten — die größte Bevölkerung",
      statIreland: "in ganz Irland, oft als MacDonnell getragen",
      countriesTitle: "Wo die McDonalds leben",
      spellingTitle: "Nach Schreibweise",
      source: "Schätzungen: Forebears (McDonald & MacDonald) und National Records of Scotland.",
    },
  },

  it: {
    name: {
      overline: "Il Nome",
      title: "Clann · Mhic · Dòmhnaill",
      intro: "Il nome gaelico del clan è composto da tre parole. Insieme dicono esattamente chi sono i McDonald — i figli del figlio di Donald.",
      terms: [
        { term: "Clann", gloss: "figli · parentela", body: "Un'antica parola gaelica per figli e discendenza — e dunque un clan: una famiglia discesa da un solo antenato." },
        { term: "Mc / Mac / Mhic", gloss: "figlio di", body: "«Figlio». Al possessivo si addolcisce in Mhic («del figlio di»); la forma femminile è Nic («figlia di»)." },
        { term: "Dòmhnall", gloss: "Donald · «signore del mondo»", body: "Il nome Donald, dal gaelico antico Domnall — domno «mondo» + val «potere». La sua forma possessiva è Dòmhnaill." },
      ],
      compareA: "Clann Dòmhnaill",
      compareAMeaning: "«i figli di Donald»",
      compareB: "Clann Mhic Dhòmhnaill",
      compareBMeaning: "«i figli del figlio di Donald» — i discendenti di MacDonald",
      note: "Da Mac Dhòmhnaill nacquero i cognomi Macdonald e McDonald; il ramo irlandese divenne Mac Domhnaill → MacDonnell.",
    },
    distribution: {
      overline: "Distribuzione",
      title: "Un clan diffuso in tutto il mondo.",
      intro: "Contati insieme, i McDonald sono tra i nomi di origine scozzese e irlandese più numerosi al mondo.",
      worldwideLabel: "McDonald nel mondo",
      worldwideNote: "McDonald e MacDonald insieme — oltre 600.000 contando ogni grafia.",
      statScotland: "2º cognome più comune di Scozia (MacDonald + McDonald)",
      statUS: "negli Stati Uniti — la popolazione più numerosa",
      statIreland: "in tutta l'Irlanda, spesso portato come MacDonnell",
      countriesTitle: "Dove vivono i McDonald",
      spellingTitle: "Per grafia",
      source: "Stime: Forebears (McDonald e MacDonald) e National Records of Scotland.",
    },
  },

  gd: {
    name: {
      overline: "An t-Ainm",
      title: "Clann · Mhic · Dòmhnaill",
      intro: "Tha ainm Gàidhlig a' chinnidh air a thogail à trì faclan. Còmhla tha iad ag innse dìreach cò na Dòmhnallaich — clann mac Dhòmhnaill.",
      terms: [
        { term: "Clann", gloss: "clann · cinneadh", body: "Seann fhacal Gàidhlig airson cloinne is sliochd — agus mar sin cinneadh: teaghlach a thàinig bho aon sinnsear." },
        { term: "Mc / Mac / Mhic", gloss: "mac", body: "“Mac.” Anns an t-sealbhach bidh e a' sèimheachadh gu Mhic (“mac”); is e Nic am foirm boireann (“nighean”)." },
        { term: "Dòmhnall", gloss: "Dòmhnall · “riaghladair an t-saoghail”", body: "An t-ainm Dòmhnall, bhon t-seann Ghàidhlig Domnall — domno “saoghal” + val “cumhachd”. Is e Dòmhnaill am foirm sealbhach." },
      ],
      compareA: "Clann Dòmhnaill",
      compareAMeaning: "“clann Dhòmhnaill”",
      compareB: "Clann Mhic Dhòmhnaill",
      compareBMeaning: "“clann mac Dhòmhnaill” — sliochd MhicDhòmhnaill",
      note: "Bho Mac Dhòmhnaill thàinig na sloinnidhean Macdonald agus McDonald; dh'fhàs am meur Èireannach na Mac Domhnaill → MacDonnell.",
    },
    distribution: {
      overline: "Sgaoileadh",
      title: "Cinneadh sgaoilte air feadh an t-saoghail.",
      intro: "Air an cunntadh còmhla, tha na Dòmhnallaich am measg nan ainmean as lìonmhoire de thùs Albannach is Èireannach air an t-saoghal.",
      worldwideLabel: "Dòmhnallaich air feadh an t-saoghail",
      worldwideNote: "McDonald agus MacDonald còmhla — còrr is 600,000 leis gach litreachadh.",
      statScotland: "an 2na sloinneadh as cumanta an Alba (MacDonald + McDonald)",
      statUS: "anns na Stàitean Aonaichte — an sluagh as motha",
      statIreland: "air feadh na h-Èireann, gu tric mar MacDonnell",
      countriesTitle: "Càite a bheil na Dòmhnallaich a' fuireach",
      spellingTitle: "A rèir litreachaidh",
      source: "Tuairmsean: Forebears (McDonald & MacDonald) agus Clàran Nàiseanta na h-Alba.",
    },
  },
};
